import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./spaces.css";

gsap.registerPlugin(ScrollTrigger);

const PIN_SCROLL_DISTANCE_MULTIPLIER = 0.7;
const MIN_PIN_SCROLL_DISTANCE_MULTIPLIER = 0.8;
const SLIDE_TRANSITION_DURATION = 0.5;
const NAV_SCROLL_DURATION = 0.45;
const SNAP_SCRUB = true;
const SNAP_MIN_DURATION = 0.1;
const SNAP_MAX_DURATION = 0.2;
const IMAGE_ROTATE_ANGLE = 90;
const IMAGE_CLIP_PATH_START = "inset(50% 50% 50% 50% round var(--spaces-media-radius))";
const IMAGE_CLIP_PATH_END = "inset(0% 0% 0% 0% round var(--spaces-media-radius))";
const FIRST_FORWARD_ACTIVATION_THRESHOLD = 5;
const FORWARD_ACTIVATION_THRESHOLD = 0.55;
const BACKWARD_ACTIVATION_THRESHOLD = 0.65;
const LAST_SLIDE_ACTIVATION_THRESHOLD = 0.78;
const FIRST_SLIDE_ACTIVATION_THRESHOLD = 0.22;
const formatSlideNumber = (value) => String(value).padStart(2, "0");

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const readCssLengthVariable = (name) => {
  const directValue = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
  if (Number.isFinite(directValue)) {
    return directValue;
  }

  const probe = document.createElement("div");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.height = `var(${name})`;

  document.body.append(probe);
  const value = probe.getBoundingClientRect().height;
  probe.remove();

  return Number.isFinite(value) ? value : 0;
};

const getHeaderOffset = () => readCssLengthVariable("--site-header-stuck-height");
const getViewportStageHeight = () => Math.max(window.innerHeight - getHeaderOffset(), 0);
const getMaxHeight = (elements) => Math.max(...elements.map((element) => element.getBoundingClientRect().height), 0);
const isTouchViewport = () => ScrollTrigger.isTouch > 0;

const setPanelAccessibility = (panels, activeIndex) => {
  panels.forEach((panel, index) => {
    const isActive = index === activeIndex;
    panel.setAttribute("aria-hidden", isActive ? "false" : "true");

    if ("inert" in panel) {
      panel.inert = !isActive;
    }
  });
};

const setButtonState = (buttons, activeIndex, { animate = true, duration = 0.24 } = {}) => {
  buttons.forEach((button, index) => {
    const indicator = button.querySelector("[data-spaces-nav-indicator]");
    const isActive = index === activeIndex;

    button.classList.toggle("is-active", isActive);

    if (isActive) {
      button.setAttribute("aria-current", "true");
    } else {
      button.removeAttribute("aria-current");
    }

    if (!indicator) return;

    if (!animate) {
      gsap.set(indicator, { autoAlpha: isActive ? 1 : 0, scale: isActive ? 1 : 0 });
      return;
    }

    gsap.to(indicator, {
      autoAlpha: isActive ? 1 : 0,
      scale: isActive ? 1 : 0,
      duration,
      ease: isActive ? "back.out(2.4)" : "power1.inOut",
      overwrite: "auto",
    });
  });
};

const setLayerOrder = (panels, activeIndex, previousIndex = activeIndex) => {
  panels.forEach((panel, index) => {
    const zIndex = index === activeIndex ? 2 : index === previousIndex ? 1 : 0;
    gsap.set(panel, { zIndex });
  });
};

const setCurrentSlideIndex = (el, nextIndex, { animate = true } = {}) => {
  if (!el) return;

  if (!animate) {
    el.textContent = formatSlideNumber(nextIndex + 1);
    el.dataset.indexValue = `${nextIndex}`;
    gsap.set(el, { autoAlpha: 1, y: 0 });
    return;
  }

  if (el.dataset.indexValue === `${nextIndex}`) return;

  el.dataset.indexValue = `${nextIndex}`;
  el.textContent = formatSlideNumber(nextIndex + 1);

  gsap.killTweensOf(el);
  gsap.fromTo(
    el,
    { autoAlpha: 0, y: 10 },
    {
      autoAlpha: 1,
      y: 0,
      duration: 0.26,
      ease: "power2.out",
      overwrite: "auto",
    },
  );
};

const initSpaces = (el) => {
  const stage = el.querySelector("[data-spaces-stage]");
  const title = el.querySelector("[data-spaces-title]");
  const indexWrap = el.querySelector("[data-spaces-index]");
  const indexCurrent = el.querySelector("[data-spaces-index-current]");
  const indexTotal = el.querySelector("[data-spaces-index-total]");
  const glow = el.querySelector(".spaces-glow");
  const layout = el.querySelector("[data-spaces-layout]");
  const imageTrack = el.querySelector("[data-spaces-image-track]");
  const contentTrack = el.querySelector("[data-spaces-content-track]");
  const imageSlides = gsap.utils.toArray(el.querySelectorAll("[data-spaces-image-slide]"));
  const contentSlides = gsap.utils.toArray(el.querySelectorAll("[data-spaces-content-slide]"));
  const navButtons = gsap.utils.toArray(el.querySelectorAll("[data-spaces-nav-button]"));
  const nav = el.querySelector("[data-spaces-nav]");

  if (!stage || !imageTrack || !contentTrack || imageSlides.length === 0) return () => {};
  if (imageSlides.length !== contentSlides.length) return () => {};

  const slideCount = imageSlides.length;
  const steps = Math.max(slideCount - 1, 1);
  let activeIndex = 0;
  let currentTimeline = null;
  let introTimeline = null;
  let pinTrigger = null;
  let introTrigger = null;
  let scrollTween = null;
  let resizeObserver = null;
  let introPlayed = false;
  let isNavigating = false;
  const desktopMediaQuery = window.matchMedia("(min-width: 768px)");

  const cleanupFns = [];
  const syncLayoutRefresh = gsap
    .delayedCall(0.08, () => {
      syncLayout();
      ScrollTrigger.refresh();
    })
    .pause();

  const killCurrentTimeline = () => {
    if (!currentTimeline) return;
    currentTimeline.kill();
    currentTimeline = null;
  };

  const killIntroTimeline = () => {
    if (!introTimeline) return;
    introTimeline.kill();
    introTimeline = null;
  };

  const killScrollTween = () => {
    if (!scrollTween) return;
    scrollTween.kill();
    scrollTween = null;
  };

  const getPinDistanceMultiplier = () => {
    const baseDistance = steps * PIN_SCROLL_DISTANCE_MULTIPLIER;
    return Math.max(baseDistance, MIN_PIN_SCROLL_DISTANCE_MULTIPLIER);
  };

  const getStageHeight = () => Math.max(stage.getBoundingClientRect().height, 0) || getViewportStageHeight();
  const getSnapConfig = () => {
    const touchViewport = isTouchViewport();

    return {
      snapTo: 1 / steps,
      duration: { min: SNAP_MIN_DURATION, max: SNAP_MAX_DURATION },
      delay: touchViewport ? 0.08 : 0,
      directional: !touchViewport,
      inertia: false,
      ease: "power2.out",
    };
  };

  const getProgressIndex = (progress, direction = 1) => {
    const clampedProgress = clamp(progress, 0, 1);
    if (clampedProgress <= 0) return 0;
    if (clampedProgress >= 1) return slideCount - 1;

    const raw = clampedProgress * steps;
    const segmentIndex = clamp(Math.floor(raw), 0, steps - 1);
    const segmentProgress = raw - segmentIndex;

    if (direction < 0) {
      const threshold = segmentIndex === 0 ? FIRST_SLIDE_ACTIVATION_THRESHOLD : BACKWARD_ACTIVATION_THRESHOLD;
      return clamp(segmentProgress <= threshold ? segmentIndex : segmentIndex + 1, 0, slideCount - 1);
    }

    const threshold =
      segmentIndex === 0
        ? FIRST_FORWARD_ACTIVATION_THRESHOLD
        : segmentIndex === steps - 1
          ? LAST_SLIDE_ACTIVATION_THRESHOLD
          : FORWARD_ACTIVATION_THRESHOLD;
    return clamp(segmentProgress >= threshold ? segmentIndex + 1 : segmentIndex, 0, slideCount - 1);
  };

  const setSlideState = (nextIndex, { animateButtons = false, buttonDuration = 0.24 } = {}) => {
    setLayerOrder(imageSlides, nextIndex);
    setLayerOrder(contentSlides, nextIndex);

    gsap.set(imageSlides, {
      autoAlpha: 0,
      rotateX: 0,
      clipPath: IMAGE_CLIP_PATH_END,
      transformPerspective: 1200,
      transformOrigin: "50% 50%",
      pointerEvents: "none",
      force3D: true,
    });
    gsap.set(contentSlides, { autoAlpha: 0, y: 24, pointerEvents: "none" });

    gsap.set(imageSlides[nextIndex], {
      autoAlpha: 1,
      rotateX: 0,
      clipPath: IMAGE_CLIP_PATH_END,
      pointerEvents: "auto",
    });
    gsap.set(contentSlides[nextIndex], { autoAlpha: 1, y: 0, pointerEvents: "auto" });

    setPanelAccessibility(imageSlides, nextIndex);
    setPanelAccessibility(contentSlides, nextIndex);

    if (navButtons.length > 0) {
      setButtonState(navButtons, nextIndex, {
        animate: animateButtons,
        duration: buttonDuration,
      });
    }

    setCurrentSlideIndex(indexCurrent, nextIndex, { animate: animateButtons });

    activeIndex = nextIndex;
  };

  const finalizeSlideTransition = (previousIndex, nextIndex) => {
    const previousImage = imageSlides[previousIndex];
    const nextImage = imageSlides[nextIndex];
    const previousContent = contentSlides[previousIndex];
    const nextContent = contentSlides[nextIndex];

    setLayerOrder(imageSlides, nextIndex, previousIndex);
    setLayerOrder(contentSlides, nextIndex, previousIndex);
    setPanelAccessibility(imageSlides, nextIndex);
    setPanelAccessibility(contentSlides, nextIndex);

    gsap.set(previousImage, { autoAlpha: 0, rotateX: 0, clipPath: IMAGE_CLIP_PATH_END, pointerEvents: "none" });
    gsap.set(nextImage, {
      autoAlpha: 1,
      rotateX: 0,
      clipPath: IMAGE_CLIP_PATH_END,
      transformPerspective: 1200,
      transformOrigin: "50% 50%",
      pointerEvents: "auto",
      force3D: true,
    });
    gsap.set(previousContent, { autoAlpha: 0, y: 24, pointerEvents: "none" });
    gsap.set(nextContent, { autoAlpha: 1, y: 0, pointerEvents: "auto" });
  };

  const syncLayout = () => {
    const tallestContent = getMaxHeight(contentSlides);
    if (desktopMediaQuery.matches) {
      const mediaHeight = imageTrack.getBoundingClientRect().height;

      if (layout) {
        gsap.set(layout, { height: mediaHeight > 0 ? Math.ceil(mediaHeight) : "auto" });
      }

      gsap.set(contentTrack, { clearProps: "height" });
    } else {
      if (layout) {
        gsap.set(layout, { clearProps: "height" });
      }

      gsap.set(contentTrack, { height: tallestContent > 0 ? Math.ceil(tallestContent) : "auto" });
    }
  };

  const tweenWindowScrollTo = (targetY, { duration = NAV_SCROLL_DURATION, onDone } = {}) => {
    killScrollTween();

    const state = { y: window.scrollY };
    scrollTween = gsap.to(state, {
      y: clamp(targetY, 0, ScrollTrigger.maxScroll(window)),
      duration,
      ease: "power2.out",
      overwrite: "auto",
      onUpdate: () => {
        window.scrollTo({
          top: state.y,
          behavior: "auto",
        });
      },
      onComplete: () => {
        scrollTween = null;
        onDone?.();
      },
      onInterrupt: () => {
        scrollTween = null;
        onDone?.();
      },
    });
  };

  const moveSlide = (nextIndex) => {
    if (nextIndex === activeIndex || nextIndex < 0 || nextIndex >= slideCount) return;

    const previousIndex = activeIndex;
    const direction = nextIndex > previousIndex ? 1 : -1;
    const previousImage = imageSlides[previousIndex];
    const nextImage = imageSlides[nextIndex];
    const previousContent = contentSlides[previousIndex];
    const nextContent = contentSlides[nextIndex];
    const hiddenImages = imageSlides.filter((_, index) => index !== previousIndex && index !== nextIndex);
    const hiddenContent = contentSlides.filter((_, index) => index !== previousIndex && index !== nextIndex);

    killCurrentTimeline();

    if (hiddenImages.length > 0) {
      gsap.set(hiddenImages, { autoAlpha: 0, rotateX: 0, clipPath: IMAGE_CLIP_PATH_END, pointerEvents: "none" });
    }

    if (hiddenContent.length > 0) {
      gsap.set(hiddenContent, { autoAlpha: 0, y: 24, pointerEvents: "none" });
    }

    setLayerOrder(imageSlides, nextIndex, previousIndex);
    setLayerOrder(contentSlides, nextIndex, previousIndex);
    setPanelAccessibility(imageSlides, nextIndex);
    setPanelAccessibility(contentSlides, nextIndex);

    if (navButtons.length > 0) {
      setButtonState(navButtons, nextIndex, {
        animate: true,
        duration: 0.22,
      });
    }

    setCurrentSlideIndex(indexCurrent, nextIndex, { animate: true });

    gsap.set(previousImage, { autoAlpha: 1, rotateX: 0, clipPath: IMAGE_CLIP_PATH_END, pointerEvents: "none" });
    gsap.set(previousContent, { autoAlpha: 1, y: 0, pointerEvents: "none" });
    gsap.set(nextImage, {
      autoAlpha: 1,
      rotateX: direction > 0 ? IMAGE_ROTATE_ANGLE : -IMAGE_ROTATE_ANGLE,
      clipPath: IMAGE_CLIP_PATH_START,
      transformPerspective: 1200,
      transformOrigin: "50% 50%",
      pointerEvents: "auto",
      force3D: true,
    });
    gsap.set(nextContent, { autoAlpha: 0, y: 24, pointerEvents: "auto" });

    activeIndex = nextIndex;

    currentTimeline = gsap.timeline({
      defaults: { ease: "power2.out" },
      onComplete: () => {
        currentTimeline = null;
        finalizeSlideTransition(previousIndex, nextIndex);
      },
    });

    currentTimeline.to(
      previousImage,
      {
        autoAlpha: 0,
        duration: SLIDE_TRANSITION_DURATION * 0.55,
        ease: "power1.out",
      },
      0,
    );

    currentTimeline.to(
      previousContent,
      {
        autoAlpha: 0,
        y: -18,
        duration: SLIDE_TRANSITION_DURATION * 0.45,
        ease: "power1.out",
      },
      0,
    );

    currentTimeline.to(
      nextImage,
      {
        rotateX: 0,
        clipPath: IMAGE_CLIP_PATH_END,
        duration: SLIDE_TRANSITION_DURATION,
        ease: "power3.out",
      },
      0,
    );

    currentTimeline.to(
      nextContent,
      {
        autoAlpha: 1,
        y: 0,
        duration: SLIDE_TRANSITION_DURATION * 0.6,
        ease: "power2.out",
      },
      0.16,
    );
  };

  const updateSlide = (nextIndex) => {
    if (nextIndex === activeIndex) return;
    moveSlide(nextIndex);
  };

  const getTargetScrollY = (index) => {
    if (pinTrigger) {
      const progress = steps > 0 ? index / steps : 0;
      return pinTrigger.start + (pinTrigger.end - pinTrigger.start) * progress;
    }

    return stage.getBoundingClientRect().top + window.scrollY;
  };

  const handleNavClick = (index) => {
    if (index < 0 || index >= slideCount) return;

    killScrollTween();
    isNavigating = true;
    moveSlide(index);
    tweenWindowScrollTo(getTargetScrollY(index), {
      onDone: () => {
        isNavigating = false;
      },
    });
  };

  const isStageInViewport = () => {
    const rect = stage.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > getHeaderOffset();
  };

  const setIntroInitialState = () => {
    if (glow) gsap.set(glow, { autoAlpha: 0 });
    if (indexWrap) gsap.set(indexWrap, { autoAlpha: 0, y: 18 });
    if (title) gsap.set(title, { autoAlpha: 0, y: 28 });
    if (imageTrack) gsap.set(imageTrack, { autoAlpha: 0, y: 32 });
    if (nav) gsap.set(nav, { autoAlpha: 0, y: 24 });
    if (contentTrack) gsap.set(contentTrack, { autoAlpha: 0, y: 24 });
  };

  const playStageIntro = () => {
    if (introPlayed) return;

    introPlayed = true;
    killIntroTimeline();

    introTimeline = gsap.timeline({
      defaults: { ease: "power2.out" },
      onComplete: () => {
        introTimeline = null;
      },
    });

    if (glow) {
      introTimeline.to(
        glow,
        {
          autoAlpha: 0.7,
          duration: 0.45,
        },
        0,
      );
    }

    if (indexWrap) {
      introTimeline.to(
        indexWrap,
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.3,
        },
        0,
      );
    }

    if (title) {
      introTimeline.to(
        title,
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.45,
        },
        0.06,
      );
    }

    introTimeline.to(
      imageTrack,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.5,
      },
      0.12,
    );

    if (nav) {
      introTimeline.to(
        nav,
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.36,
        },
        0.18,
      );
    }

    introTimeline.to(
      contentTrack,
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.42,
      },
      0.2,
    );
  };

  el.classList.add("spaces-ready");
  syncLayout();
  setSlideState(0);
  if (indexTotal) {
    indexTotal.textContent = formatSlideNumber(slideCount);
  }
  setIntroInitialState();

  navButtons.forEach((button, index) => {
    const onClick = () => handleNavClick(index);
    button.addEventListener("click", onClick);
    cleanupFns.push(() => {
      button.removeEventListener("click", onClick);
    });
  });

  if (typeof ResizeObserver === "function") {
    resizeObserver = new ResizeObserver(() => {
      syncLayoutRefresh.restart(true);
    });

    resizeObserver.observe(imageTrack);
    contentSlides.forEach((slide) => {
      resizeObserver.observe(slide);
    });
  }

  let lastViewportWidth = window.innerWidth;
  let lastViewportHeight = window.innerHeight;
  const onResize = () => {
    const nextViewportWidth = window.innerWidth;
    const nextViewportHeight = window.innerHeight;
    const widthChanged = nextViewportWidth !== lastViewportWidth;
    const heightChanged = nextViewportHeight !== lastViewportHeight;

    lastViewportWidth = nextViewportWidth;
    lastViewportHeight = nextViewportHeight;

    if (!widthChanged && (!heightChanged || isTouchViewport())) return;

    syncLayoutRefresh.restart(true);
  };

  window.addEventListener("resize", onResize);
  window.addEventListener("load", onResize, { once: true });
  cleanupFns.push(() => {
    window.removeEventListener("resize", onResize);
    window.removeEventListener("load", onResize);
  });

  if (slideCount > 1) {
    pinTrigger = ScrollTrigger.create({
      trigger: stage,
      pin: true,
      anticipatePin: 1,
      scrub: SNAP_SCRUB,
      start: () => `top ${getHeaderOffset()}px`,
      end: () => `+=${getStageHeight() * getPinDistanceMultiplier()}`,
      snap: getSnapConfig(),
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        if (isNavigating) return;
        const nextIndex = getProgressIndex(self.progress, self.direction);
        updateSlide(nextIndex);
      },
      onEnter: () => {
        playStageIntro();
      },
      onEnterBack: () => {
        playStageIntro();
      },
      onRefresh: (self) => {
        const nextIndex = getProgressIndex(self.progress, self.direction);
        killCurrentTimeline();
        setSlideState(nextIndex);
      },
      onLeave: () => {
        killCurrentTimeline();
        setSlideState(slideCount - 1);
      },
      onLeaveBack: () => {
        killCurrentTimeline();
        setSlideState(0);
      },
    });
  } else {
    introTrigger = ScrollTrigger.create({
      trigger: stage,
      start: "top bottom",
      end: () => `bottom ${getHeaderOffset()}px`,
      invalidateOnRefresh: true,
      onEnter: () => {
        playStageIntro();
      },
      onEnterBack: () => {
        playStageIntro();
      },
      onRefresh: (self) => {
        if (self.isActive || isStageInViewport()) {
          playStageIntro();
        }
      },
    });

    if (isStageInViewport()) {
      playStageIntro();
    }
  }

  return () => {
    syncLayoutRefresh.kill();
    killCurrentTimeline();
    killIntroTimeline();
    killScrollTween();
    pinTrigger?.kill();
    introTrigger?.kill();
    resizeObserver?.disconnect();
    cleanupFns.forEach((cleanup) => cleanup());
  };
};

document.addEventListener("DOMContentLoaded", () => {
  const mm = gsap.matchMedia();

  mm.add("(prefers-reduced-motion: no-preference)", () => {
    const cleanups = [];

    document.querySelectorAll(".element-spaces").forEach((el) => {
      let instanceCleanup;
      const ctx = gsap.context(() => {
        instanceCleanup = initSpaces(el);
      }, el);

      cleanups.push(() => {
        instanceCleanup?.();
        ctx.revert();
        el.classList.remove("spaces-ready");
        el.querySelector("[data-spaces-layout]")?.style.removeProperty("height");
        el.querySelector("[data-spaces-content-track]")?.style.removeProperty("height");

        el.querySelectorAll("[data-spaces-image-slide], [data-spaces-content-slide]").forEach((slide) => {
          slide.removeAttribute("aria-hidden");
          if ("inert" in slide) slide.inert = false;
        });

        el.querySelectorAll("[data-spaces-nav-button]").forEach((button) => {
          button.classList.remove("is-active");
          button.removeAttribute("aria-current");
        });
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  });
});
